import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import VersionBadge from '@/components/VersionBadge';
import PublicSeriesCard from '@/components/PublicSeriesCard';
import {
  User, BookOpen, Heart, Users, Calendar, Library, Loader2,
  UserPlus, UserCheck, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { getBookTypeLabel } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 20;

// Helper to get full image URL (handles both local /static/ paths and external URLs)
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const PublicUserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [stories, setStories] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [storiesLoadingMore, setStoriesLoadingMore] = useState(false);
  const [storiesOffset, setStoriesOffset] = useState(0);
  const [storiesHasMore, setStoriesHasMore] = useState(true);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesLoadingMore, setSeriesLoadingMore] = useState(false);
  const [seriesOffset, setSeriesOffset] = useState(0);
  const [seriesHasMore, setSeriesHasMore] = useState(true);
  const [activeTab, setActiveTab] = useState('stories');
  const [sort, setSort] = useState('new');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const storiesLoaderRef = useRef(null);
  const seriesLoaderRef = useRef(null);

  const sortOptions = [
    { value: 'new', label: 'Newest' },
    { value: 'popular', label: 'Most Popular' },
  ];

  useEffect(() => {
    loadProfile();
    checkAuth();
  }, [userId]);

  useEffect(() => {
    if (profile) {
      if (activeTab === 'stories') {
        // Reset and load stories
        setStories([]);
        setStoriesOffset(0);
        setStoriesHasMore(true);
        loadStories(0, true);
      } else {
        // Reset and load series
        setSeries([]);
        setSeriesOffset(0);
        setSeriesHasMore(true);
        loadSeries(0, true);
      }
    }
  }, [activeTab, sort, profile]);

  // Infinite scroll for stories
  useEffect(() => {
    if (activeTab !== 'stories') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && storiesHasMore && !storiesLoading && !storiesLoadingMore) {
          loadMoreStories();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentLoader = storiesLoaderRef.current;
    if (currentLoader) observer.observe(currentLoader);

    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [storiesHasMore, storiesLoading, storiesLoadingMore, storiesOffset, activeTab]);

  // Infinite scroll for series
  useEffect(() => {
    if (activeTab !== 'series') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && seriesHasMore && !seriesLoading && !seriesLoadingMore) {
          loadMoreSeries();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentLoader = seriesLoaderRef.current;
    if (currentLoader) observer.observe(currentLoader);

    return () => {
      if (currentLoader) observer.unobserve(currentLoader);
    };
  }, [seriesHasMore, seriesLoading, seriesLoadingMore, seriesOffset, activeTab]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch {
      setCurrentUser(null);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/profile/${userId}`, {
        withCredentials: true
      });
      setProfile(response.data);
      setFollowing(response.data.is_following);
      setFollowersCount(response.data.followers_count || 0);
    } catch (error) {
      console.error('Failed to load profile:', error);
      if (error.response?.status === 404) {
        toast.error('User not found');
        navigate('/explore');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStories = async (offset = 0, isReset = false) => {
    if (isReset) {
      setStoriesLoading(true);
    } else {
      setStoriesLoadingMore(true);
    }

    try {
      const response = await axios.get(
        `${API}/profile/${userId}/stories?sort=${sort}&limit=${PAGE_SIZE}&offset=${offset}`,
        { withCredentials: true }
      );
      const newStories = response.data.stories || [];
      setStoriesHasMore(response.data.has_more);

      if (isReset) {
        setStories(newStories);
      } else {
        setStories(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const uniqueNew = newStories.filter(s => !existingIds.has(s.id));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (error) {
      console.error('Failed to load stories:', error);
    } finally {
      setStoriesLoading(false);
      setStoriesLoadingMore(false);
    }
  };

  const loadMoreStories = () => {
    const newOffset = storiesOffset + PAGE_SIZE;
    setStoriesOffset(newOffset);
    loadStories(newOffset, false);
  };

  const loadSeries = async (offset = 0, isReset = false) => {
    if (isReset) {
      setSeriesLoading(true);
    } else {
      setSeriesLoadingMore(true);
    }

    try {
      const response = await axios.get(
        `${API}/profile/${userId}/series?limit=${PAGE_SIZE}&offset=${offset}`,
        { withCredentials: true }
      );
      const newSeries = response.data.series || [];
      setSeriesHasMore(response.data.has_more);

      if (isReset) {
        setSeries(newSeries);
      } else {
        setSeries(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const uniqueNew = newSeries.filter(s => !existingIds.has(s.id));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (error) {
      console.error('Failed to load series:', error);
    } finally {
      setSeriesLoading(false);
      setSeriesLoadingMore(false);
    }
  };

  const loadMoreSeries = () => {
    const newOffset = seriesOffset + PAGE_SIZE;
    setSeriesOffset(newOffset);
    loadSeries(newOffset, false);
  };

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error('Please sign in to follow users');
      navigate('/login');
      return;
    }

    setFollowLoading(true);
    try {
      const response = await axios.post(`${API}/profile/${userId}/follow`, {}, {
        withCredentials: true
      });
      setFollowing(response.data.is_following);
      setFollowersCount(response.data.followers_count);
      toast.success(response.data.is_following ? 'Following!' : 'Unfollowed');
    } catch (error) {
      console.error('Failed to follow:', error);
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toString() || '0';
  };

  // Story card component
  const StoryCard = ({ story }) => (
    <div
      className="flex items-center gap-4 p-3 hover:scale-[1.01] transition-all duration-200 cursor-pointer group"
      onClick={() => navigate(`/story/${story.id}`)}
      style={{ borderBottom: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--st-bg-hover)'}
      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--st-bg-surface)'}
    >
      {/* Thumbnail */}
      <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0">
        {story.thumbnail ? (
          <img src={getImageUrl(story.thumbnail)} alt={story.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
            <BookOpen className="w-6 h-6" style={{ color: 'var(--st-primary-hover)' }} />
          </div>
        )}
        {story.page_count > 0 && (
          <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-xs font-bold text-white" style={{ background: 'rgba(0,0,0,0.6)', fontSize: '10px' }}>
            {story.page_count}p
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-bold text-sm md:text-base truncate" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
            {story.title}
          </h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0" style={{ background: 'var(--st-bg-muted)', color: 'var(--st-text-heading)' }}>
            {getBookTypeLabel(story.age_range)}
          </span>
          <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
          {story.series_title && (
            <Library className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#4F46E5' }} />
          )}
        </div>
        <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
          {new Date(story.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </p>
      </div>

      {/* Stats */}
      <div className="hidden md:flex items-center gap-4 text-xs flex-shrink-0" style={{ color: 'var(--st-text-muted)' }}>
        <span className="flex items-center gap-1">
          <Heart className="w-4 h-4" fill={story.is_favorited ? 'var(--st-primary)' : 'none'} stroke={story.is_favorited ? 'var(--st-primary)' : 'currentColor'} />
          {story.favorites_count || 0}
        </span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PageLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading profile...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!profile) {
    return (
      <PageLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <User className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
            <p className="font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>User not found</p>
            <button
              onClick={() => navigate('/explore')}
              className="text-sm font-semibold hover:underline"
              style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-primary)' }}
            >
              Back to Explore
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const isOwnProfile = profile.is_own_profile;

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-screen-xl mx-auto px-4 py-4 md:py-6 md:px-8">
          {/* Profile Header Card */}
          <div className="rounded-2xl p-6 mb-5 shadow-sm" style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}>
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {profile.picture ? (
                  <img
                    src={getImageUrl(profile.picture)}
                    alt={profile.name}
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover mx-auto md:mx-0"
                    style={{ border: '4px solid var(--st-border-light)' }}
                  />
                ) : (
                  <div
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full flex items-center justify-center mx-auto md:mx-0"
                    style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)', border: '4px solid var(--st-border-light)' }}
                  >
                    <User className="w-12 h-12 md:w-16 md:h-16" style={{ color: 'var(--st-primary)' }} />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                    {profile.name || 'Anonymous'}
                  </h1>
                  {!isOwnProfile && (
                    <button
                      onClick={handleFollow}
                      disabled={followLoading}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                      style={{
                        background: following
                          ? 'var(--st-bg-surface)'
                          : 'linear-gradient(135deg, var(--st-primary) 0%, var(--st-primary-hover) 100%)',
                        color: following ? 'var(--st-text-heading)' : 'var(--st-bg-page)',
                        border: following ? '1px solid var(--st-border-light)' : 'none',
                        fontFamily: 'Quicksand, sans-serif'
                      }}
                    >
                      {followLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : following ? (
                        <>
                          <UserCheck className="w-4 h-4" />
                          Following
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          Follow
                        </>
                      )}
                    </button>
                  )}
                  {isOwnProfile && (
                    <button
                      onClick={() => navigate('/profile')}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all"
                      style={{
                        background: 'var(--st-bg-surface)',
                        border: '1px solid var(--st-border-light)',
                        color: 'var(--st-text-heading)',
                        fontFamily: 'Quicksand, sans-serif'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--st-bg-hover)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--st-bg-surface)'}
                    >
                      Edit Profile
                    </button>
                  )}
                </div>

                {profile.handle && (
                  <p className="text-sm mb-2" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    @{profile.handle}
                  </p>
                )}

                {profile.bio && (
                  <p className="text-sm mb-4 max-w-xl" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-body)' }}>
                    {profile.bio}
                  </p>
                )}

                {/* Join Date */}
                <div className="flex items-center justify-center md:justify-start gap-1 text-xs mb-4" style={{ color: 'var(--st-text-muted)' }}>
                  <Calendar className="w-3.5 h-3.5" />
                  <span style={{ fontFamily: 'Quicksand, sans-serif' }}>Joined {formatDate(profile.created_at)}</span>
                </div>

                {/* Stats Row */}
                <div className="flex items-center justify-center md:justify-start gap-6">
                  <div className="text-center">
                    <p className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                      {formatNumber(profile.story_count)}
                    </p>
                    <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Stories</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                      {formatNumber(profile.series_count)}
                    </p>
                    <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Series</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-primary)' }}>
                      {formatNumber(profile.favorites_received_count)}
                    </p>
                    <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Favorites</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                      {formatNumber(followersCount)}
                    </p>
                    <p className="text-xs" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Followers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="rounded-2xl p-4 mb-5 shadow-sm" style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              {/* Tabs */}
              <div className="flex items-center gap-2 p-1 rounded-xl" style={{ background: 'var(--st-bg-muted)' }}>
                <button
                  onClick={() => setActiveTab('stories')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: activeTab === 'stories' ? 'var(--st-text-heading)' : 'var(--st-text-muted)',
                    background: activeTab === 'stories' ? 'var(--st-bg-surface)' : 'transparent',
                    boxShadow: activeTab === 'stories' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                >
                  <BookOpen className="w-4 h-4" style={{ color: activeTab === 'stories' ? 'var(--st-primary)' : 'var(--st-text-muted)' }} />
                  Stories ({profile.story_count || 0})
                </button>
                <button
                  onClick={() => setActiveTab('series')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: activeTab === 'series' ? 'var(--st-text-heading)' : 'var(--st-text-muted)',
                    background: activeTab === 'series' ? 'var(--st-bg-surface)' : 'transparent',
                    boxShadow: activeTab === 'series' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                >
                  <Library className="w-4 h-4" style={{ color: activeTab === 'series' ? '#4F46E5' : 'var(--st-text-muted)' }} />
                  Series ({profile.series_count || 0})
                </button>
              </div>

              {/* Sort - only for stories */}
              {activeTab === 'stories' && (
                <div className="relative">
                  <button
                    onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200"
                    style={{
                      fontFamily: 'Quicksand, sans-serif',
                      background: 'var(--st-bg-muted)',
                      color: 'var(--st-text-heading)'
                    }}
                  >
                    <span>{sortOptions.find(o => o.value === sort)?.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--st-text-muted)' }} />
                  </button>

                  {sortDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setSortDropdownOpen(false)} />
                      <div
                        className="absolute right-0 top-full mt-2 z-20 rounded-xl shadow-lg overflow-hidden"
                        style={{ border: '1px solid var(--st-border-light)', minWidth: '140px', background: 'var(--st-bg-surface)' }}
                      >
                        {sortOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => {
                              setSort(option.value);
                              setSortDropdownOpen(false);
                            }}
                            className="w-full px-4 py-2.5 text-left text-xs font-semibold transition-colors flex items-center gap-2"
                            style={{
                              fontFamily: 'Quicksand, sans-serif',
                              color: sort === option.value ? 'var(--st-primary)' : 'var(--st-text-heading)',
                              background: 'var(--st-bg-surface)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--st-bg-hover)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--st-bg-surface)'}
                          >
                            {sort === option.value && <span style={{ color: 'var(--st-primary)' }}>✓</span>}
                            <span className={sort === option.value ? '' : 'ml-5'}>{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          {activeTab === 'stories' ? (
            storiesLoading ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}>
                <BookOpen className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: 'var(--st-primary)' }} />
                <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading stories...</p>
              </div>
            ) : stories.length === 0 ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}>
                <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--st-border-medium)' }} />
                <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  No public stories yet
                </p>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  {isOwnProfile ? 'Create and publish your first story!' : 'This user hasn\'t published any stories yet.'}
                </p>
              </div>
            ) : (
              <>
                <div className="rounded-2xl overflow-hidden shadow-sm" style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}>
                  {stories.map((story) => (
                    <StoryCard key={story.id} story={story} />
                  ))}
                </div>

                {/* Infinite scroll loader for stories */}
                {storiesHasMore && (
                  <div ref={storiesLoaderRef} className="flex justify-center py-8">
                    {storiesLoadingMore && (
                      <div className="flex items-center gap-2" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--st-primary)' }} />
                        <span className="text-sm">Loading more stories...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* End of stories indicator */}
                {!storiesHasMore && stories.length > 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                      You've seen all stories!
                    </p>
                  </div>
                )}
              </>
            )
          ) : (
            seriesLoading ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}>
                <Library className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: '#4F46E5' }} />
                <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading series...</p>
              </div>
            ) : series.length === 0 ? (
              <div className="text-center py-16 rounded-2xl shadow-sm" style={{ border: '1px solid var(--st-border-light)', background: 'var(--st-bg-surface)' }}>
                <Library className="w-12 h-12 mx-auto mb-4" style={{ color: '#C7D2FE' }} />
                <p className="text-lg font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  No public series yet
                </p>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  {isOwnProfile ? 'Create and publish your first series!' : 'This user hasn\'t published any series yet.'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {series.map((s) => (
                    <PublicSeriesCard key={s.id} series={s} />
                  ))}
                </div>

                {/* Infinite scroll loader for series */}
                {seriesHasMore && (
                  <div ref={seriesLoaderRef} className="flex justify-center py-8">
                    {seriesLoadingMore && (
                      <div className="flex items-center gap-2" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#4F46E5' }} />
                        <span className="text-sm">Loading more series...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* End of series indicator */}
                {!seriesHasMore && series.length > 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                      You've seen all series!
                    </p>
                  </div>
                )}
              </>
            )
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default PublicUserProfilePage;
